
import React from 'react';
import { CalendarEvent } from '@/types/calendar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { addDays, isBefore, isAfter, startOfDay, endOfDay, isToday, isTomorrow, isThisWeek, isWithinInterval } from 'date-fns';
import { CalendarCheck, CalendarClock } from 'lucide-react';
import EventItem from './EventItem';

interface UpcomingEventsProps {
  events: CalendarEvent[];
  onEventUpdate: (event: CalendarEvent) => void;
  onEventDelete: (eventId: string) => void;
}

const UpcomingEvents: React.FC<UpcomingEventsProps> = ({ events, onEventUpdate, onEventDelete }) => {
  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);
  const tomorrowStart = startOfDay(addDays(now, 1));
  const tomorrowEnd = endOfDay(addDays(now, 1));
  const weekEnd = endOfDay(addDays(now, 7));
  
  const todayEvents = events.filter(event => {
    const eventStart = new Date(event.start);
    return isToday(eventStart);
  }).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  
  const tomorrowEvents = events.filter(event => {
    const eventStart = new Date(event.start);
    return isTomorrow(eventStart);
  }).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  
  const thisWeekEvents = events.filter(event => {
    const eventStart = new Date(event.start);
    return isThisWeek(eventStart) && !isToday(eventStart) && !isTomorrow(eventStart);
  }).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  
  const upcomingEvents = events.filter(event => {
    const eventStart = new Date(event.start);
    return isAfter(eventStart, weekEnd);
  }).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  
  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <CalendarCheck className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-semibold">Upcoming Events</h2>
      </div>
      
      <Tabs defaultValue="today">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="today">
            Today
            {todayEvents.length > 0 && (
              <span className="ml-1 text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5">
                {todayEvents.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="tomorrow">
            Tomorrow
            {tomorrowEvents.length > 0 && (
              <span className="ml-1 text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5">
                {tomorrowEvents.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="week">
            This Week
            {thisWeekEvents.length > 0 && (
              <span className="ml-1 text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5">
                {thisWeekEvents.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="upcoming">
            Later
            {upcomingEvents.length > 0 && (
              <span className="ml-1 text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5">
                {upcomingEvents.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="today">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Today's Schedule</CardTitle>
              <CardDescription>
                {todayEvents.length === 0 
                  ? "You don't have any events scheduled for today" 
                  : `You have ${todayEvents.length} event${todayEvents.length === 1 ? '' : 's'} today`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {todayEvents.length > 0 ? (
                    todayEvents.map(event => (
                      <EventItem 
                        key={event.id} 
                        event={event} 
                        onUpdate={onEventUpdate} 
                        onDelete={onEventDelete} 
                      />
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <CalendarClock className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Your schedule is clear for today</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="tomorrow">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tomorrow's Schedule</CardTitle>
              <CardDescription>
                {tomorrowEvents.length === 0 
                  ? "You don't have any events scheduled for tomorrow" 
                  : `You have ${tomorrowEvents.length} event${tomorrowEvents.length === 1 ? '' : 's'} tomorrow`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {tomorrowEvents.length > 0 ? (
                    tomorrowEvents.map(event => (
                      <EventItem 
                        key={event.id} 
                        event={event} 
                        onUpdate={onEventUpdate} 
                        onDelete={onEventDelete} 
                      />
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <CalendarClock className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Your schedule is clear for tomorrow</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="week">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">This Week's Schedule</CardTitle>
              <CardDescription>
                {thisWeekEvents.length === 0 
                  ? "You don't have any events scheduled for this week" 
                  : `You have ${thisWeekEvents.length} event${thisWeekEvents.length === 1 ? '' : 's'} this week`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {thisWeekEvents.length > 0 ? (
                    thisWeekEvents.map(event => (
                      <EventItem 
                        key={event.id} 
                        event={event} 
                        onUpdate={onEventUpdate} 
                        onDelete={onEventDelete}
                        showDate
                      />
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <CalendarClock className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Your schedule is clear for the rest of the week</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="upcoming">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upcoming Events</CardTitle>
              <CardDescription>
                {upcomingEvents.length === 0 
                  ? "You don't have any upcoming events" 
                  : `You have ${upcomingEvents.length} upcoming event${upcomingEvents.length === 1 ? '' : 's'}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {upcomingEvents.length > 0 ? (
                    upcomingEvents.map(event => (
                      <EventItem 
                        key={event.id} 
                        event={event} 
                        onUpdate={onEventUpdate} 
                        onDelete={onEventDelete}
                        showDate
                      />
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <CalendarClock className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No upcoming events scheduled</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UpcomingEvents;
